import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CATEGORIES = [
  'business', 'leadership', 'marketing', 'sales', 'finance',
  'strategy', 'management', 'productivity', 'entrepreneurship', 'technology'
];

const BookEditModal = ({ book, onSave, onClose, isSaving }) => {
  const isNew = !book?.id;
  const [form, setForm] = useState({
    title: '',
    author: '',
    description: '',
    price: '',
    category: 'business',
    cover_image: '',
    cover_image_alt: '',
    external_link: '',
    is_new: false,
    is_bestseller: false,
    frameworks: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (book) {
      setForm({
        title: book?.title || '',
        author: book?.author || '',
        description: book?.description || '',
        price: book?.price?.toString() || '',
        category: book?.category || 'business',
        cover_image: book?.cover_image || '',
        cover_image_alt: book?.cover_image_alt || '',
        external_link: book?.external_link || '',
        is_new: book?.is_new || false,
        is_bestseller: book?.is_bestseller || false,
        frameworks: Array.isArray(book?.frameworks) ? book?.frameworks?.join(', ') : (book?.frameworks || ''),
      });
    }
  }, [book]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form?.title?.trim()) newErrors.title = 'Title is required';
    if (!form?.author?.trim()) newErrors.author = 'Author is required';
    if (!form?.description?.trim()) newErrors.description = 'Description is required';
    if (!form?.price || isNaN(Number(form?.price)) || Number(form?.price) < 0) {
      newErrors.price = 'Valid price is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validate()) return;
    const frameworksArray = form?.frameworks
      ? form?.frameworks?.split(',')?.map((f) => f?.trim())?.filter(Boolean)
      : [];
    onSave?.({
      ...form,
      price: parseFloat(form?.price),
      frameworks: frameworksArray,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {isNew ? 'Add New Book' : 'Edit Book'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Title *</label>
              <Input
                value={form?.title}
                onChange={(e) => handleChange('title', e?.target?.value)}
                placeholder="Book title"
                className={errors?.title ? 'border-error' : ''}
              />
              {errors?.title && <p className="text-error text-xs mt-1">{errors?.title}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Author *</label>
              <Input
                value={form?.author}
                onChange={(e) => handleChange('author', e?.target?.value)}
                placeholder="Author name"
                className={errors?.author ? 'border-error' : ''}
              />
              {errors?.author && <p className="text-error text-xs mt-1">{errors?.author}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Price ($) *</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form?.price}
                onChange={(e) => handleChange('price', e?.target?.value)}
                placeholder="9.99"
                className={errors?.price ? 'border-error' : ''}
              />
              {errors?.price && <p className="text-error text-xs mt-1">{errors?.price}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Category</label>
              <select
                value={form?.category}
                onChange={(e) => handleChange('category', e?.target?.value)}
                className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {CATEGORIES?.map((cat) => (
                  <option key={cat} value={cat}>{cat?.charAt(0)?.toUpperCase() + cat?.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Cover Image URL</label>
              <Input
                value={form?.cover_image}
                onChange={(e) => handleChange('cover_image', e?.target?.value)}
                placeholder="/assets/images/cover.jpg"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Cover Image Alt Text</label>
              <Input
                value={form?.cover_image_alt}
                onChange={(e) => handleChange('cover_image_alt', e?.target?.value)}
                placeholder="Descriptive alt text for the cover image"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Description *</label>
              <textarea
                value={form?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                placeholder="Book description..."
                rows={4}
                className={`w-full px-3 py-2 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none ${
                  errors?.description ? 'border-error' : 'border-border'
                }`}
              />
              {errors?.description && <p className="text-error text-xs mt-1">{errors?.description}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Topics / Frameworks</label>
              <Input
                value={form?.frameworks}
                onChange={(e) => handleChange('frameworks', e?.target?.value)}
                placeholder="Strategic Planning, Leadership, Management (comma-separated)"
              />
              <p className="text-xs text-muted-foreground mt-1">Separate multiple topics with commas</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">External Link</label>
              <Input
                value={form?.external_link}
                onChange={(e) => handleChange('external_link', e?.target?.value)}
                placeholder="https://example.com"
              />
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form?.is_new}
                  onChange={(e) => handleChange('is_new', e?.target?.checked)}
                  className="rounded border-border"
                />
                <span className="text-sm text-foreground">Mark as New</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form?.is_bestseller}
                  onChange={(e) => handleChange('is_bestseller', e?.target?.checked)}
                  className="rounded border-border"
                />
                <span className="text-sm text-foreground">Mark as Bestseller</span>
              </label>
            </div>
          </div>

          {form?.cover_image && (
            <div className="border border-border rounded-lg p-4 bg-muted/20">
              <p className="text-xs text-muted-foreground mb-2">Cover Preview</p>
              <img
                src={form?.cover_image}
                alt={form?.cover_image_alt || 'Book cover preview'}
                className="h-32 w-auto object-cover rounded shadow-sm"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <Button variant="outline" type="button" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Saving...
                </span>
              ) : (
                isNew ? 'Add Book' : 'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookEditModal;
